import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { copy, pageTitles } from "./copy";

const STORAGE_KEY = "kss-language-v2";
const LanguageContext = createContext(null);

const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "ko" ? "ko" : "en";
  } catch {
    return "en";
  }
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStored);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable */
    }
  }, [lang]);

  const setLang = useCallback((next) => setLangState(next === "ko" ? "ko" : "en"), []);
  const t = useCallback((key) => copy[lang][key] ?? copy.en[key] ?? key, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, isKo: lang === "ko" }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};

/** Sets document.title for the current page in the active language. */
export const usePageTitle = (page) => {
  const { lang } = useLang();
  useEffect(() => {
    document.title = pageTitles[page]?.[lang] ?? pageTitles.home[lang];
  }, [page, lang]);
};

/**
 * Renders a copy key. Approved marketing copy contains deliberate <br>/<span>/<strong>
 * line groups, so values are injected as HTML. Only trusted dictionary strings reach here.
 */
export function T({ k, as: Tag = "span", className, ...rest }) {
  const { t } = useLang();
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: t(k) }} {...rest} />;
}
